import React from 'react';
import { Body7, Container } from '@ui';
import { Select, NumberField, TextField } from '@ui/inputs';
import { createTimeInstance } from '@utils/time';
import { SpecEditorContext } from './SpecEditorContext';
import { DATE_MODES, dateModeOf, dateProvidesFor } from './specSchema';
import { Consequence, MicroLabel, Row } from './styles';

// The date_range builder.
//
// The mode is DERIVED from the tokens rather than stored, and switching mode rewrites both
// endpoints. That derivation is what turns the anchor rules from warnings into structure:
// `now` mode never emits an `anchor` key, anchored mode always emits one, and the anchor select's
// entire option list is the trigger's Provides set — so an anchor naming a field the trigger does
// not provide is not something this control can express.
//
// The option list is the Provides set exactly as the loader checks it: the documented rule is that
// `trigger.<field>` must appear in the trigger's Provides, and nothing narrows that to date-typed
// fields. We offer what the rule allows and say what the endpoint means, rather than inventing a
// narrower rule the engine does not have.

const offsetOf = (token, prefix) => {
  const match = new RegExp(`^${prefix}([+-])(\\d+)d$`).exec(String(token || ''));
  if (!match) return null;
  return Number(match[1] === '-' ? -match[2] : match[2]);
};

const toToken = (prefix, days) => {
  if (days === null || days === undefined || Number.isNaN(Number(days))) return prefix;
  const n = Number(days);
  if (n === 0) return prefix;
  return `${prefix}${n < 0 ? '-' : '+'}${Math.abs(n)}d`;
};

const resolveNow = (token) => {
  const days = offsetOf(token, 'now');
  const base = createTimeInstance();
  const resolved = days === null ? base : base.add(days, 'day');
  return resolved.format('MMM D, YYYY');
};

export const DateRangeControl = ({ basePath }) => {
  const { definition, provides, setValue, removeValue } = React.useContext(SpecEditorContext);

  // The loader only checks that the anchor names SOMETHING in Provides, so anchoring a window on a
  // company reference compiles — it is just meaningless. Offer the date-typed fields first and
  // default to one, rather than to whatever happens to come first in the Provides list.
  const dateFields = dateProvidesFor((definition || {}).trigger && definition.trigger.kind);

  const documents = ((definition || {}).document_set || {}).documents || {};
  const range = documents.date_range || {};
  const mode = dateModeOf(range);

  const setMode = (nextMode) => {
    if (nextMode === mode) return;

    const fromDays = offsetOf(range.from, mode === DATE_MODES.ANCHORED ? 'T' : 'now');
    const toDays = offsetOf(range.to, mode === DATE_MODES.ANCHORED ? 'T' : 'now');

    if (nextMode === DATE_MODES.NOW) {
      setValue(`${basePath}.from`, toToken('now', fromDays === null ? -90 : fromDays));
      setValue(`${basePath}.to`, toToken('now', toDays === null ? 0 : toDays));
      removeValue(`${basePath}.anchor`);
      return;
    }
    if (nextMode === DATE_MODES.ANCHORED) {
      setValue(`${basePath}.from`, toToken('T', fromDays === null ? -32 : fromDays));
      setValue(`${basePath}.to`, toToken('T', toDays === null ? -2 : toDays));
      setValue(`${basePath}.anchor`, `trigger.${dateFields[0] || provides[0] || ''}`);
      return;
    }
    setValue(`${basePath}.from`, createTimeInstance().add(-90, 'day').format('YYYY-MM-DD'));
    setValue(`${basePath}.to`, createTimeInstance().format('YYYY-MM-DD'));
    removeValue(`${basePath}.anchor`);
  };

  const anchorOptions = provides.map((field) => ({ value: `trigger.${field}`, label: `trigger.${field}` }));
  const anchorField = range.anchor ? String(range.anchor).replace(/^trigger\./, '') : null;
  const anchorStranded = !!anchorField && provides.indexOf(anchorField) === -1;

  return (
    <div>
      <MicroLabel>Window</MicroLabel>
      <Container top={8}>
        <Row>
          <Select
            label="How the window is expressed"
            value={mode}
            onChange={setMode}
            options={[
              { value: DATE_MODES.NOW, label: 'Relative to today (now ± N days)' },
              {
                value: DATE_MODES.ANCHORED,
                label: 'Anchored to a trigger field (T ± N days)',
                disabled: provides.length === 0,
                reason: provides.length === 0 ? 'this trigger provides nothing to anchor to' : undefined,
              },
              { value: DATE_MODES.ABSOLUTE, label: 'Fixed calendar dates' },
            ]}
          />
        </Row>
      </Container>

      <Container top={16}>
        {mode === DATE_MODES.ABSOLUTE ? (
          <Row $align="flex-start">
            <TextField
              type="date"
              label="From"
              value={range.from || ''}
              onChange={(v) => setValue(`${basePath}.from`, v)}
            />
            <TextField type="date" label="To" value={range.to || ''} onChange={(v) => setValue(`${basePath}.to`, v)} />
          </Row>
        ) : (
          <Row $align="flex-start">
            <NumberField
              label={
                mode === DATE_MODES.ANCHORED
                  ? 'From — days either side of the anchor'
                  : 'From — days either side of today'
              }
              value={offsetOf(range.from, mode === DATE_MODES.ANCHORED ? 'T' : 'now')}
              onChange={(v) => setValue(`${basePath}.from`, toToken(mode === DATE_MODES.ANCHORED ? 'T' : 'now', v))}
              hint={`Serialises as ${range.from || '—'}`}
            />
            <NumberField
              label={
                mode === DATE_MODES.ANCHORED ? 'To — days either side of the anchor' : 'To — days either side of today'
              }
              value={offsetOf(range.to, mode === DATE_MODES.ANCHORED ? 'T' : 'now')}
              onChange={(v) => setValue(`${basePath}.to`, toToken(mode === DATE_MODES.ANCHORED ? 'T' : 'now', v))}
              hint={`Serialises as ${range.to || '—'}`}
            />
          </Row>
        )}
      </Container>

      {mode === DATE_MODES.ANCHORED && (
        <Container top={16}>
          <Select
            label="Anchor"
            value={range.anchor || ''}
            onChange={(v) => setValue(`${basePath}.anchor`, v)}
            options={
              anchorStranded
                ? anchorOptions.concat([{ value: range.anchor, label: `${range.anchor} — not provided` }])
                : anchorOptions
            }
            placeholder={range.anchor ? undefined : 'Pick what the window hangs off…'}
            invalid={anchorStranded}
            hint={
              dateFields.length
                ? 'This list is exactly what the chosen trigger provides. Change the trigger and an anchor it no longer provides is flagged rather than silently kept.'
                : 'This trigger provides no date field, so nothing here is a sensible anchor — the loader only checks that the name is in its Provides set.'
            }
          />
        </Container>
      )}

      <Container top={16}>
        {mode === DATE_MODES.NOW && (
          <Consequence>{`Today that resolves to ${resolveNow(range.from)} → ${resolveNow(
            range.to
          )}, inclusive. It moves every day.`}</Consequence>
        )}
        {mode === DATE_MODES.ANCHORED && (
          <Consequence>
            {`Resolved per occasion, relative to ${range.anchor || 'the anchor'}. Both endpoints are inclusive.`}
          </Consequence>
        )}
        {mode === DATE_MODES.ABSOLUTE && (
          <Consequence>{`Fixed at ${range.from || '—'} → ${
            range.to || '—'
          }, inclusive, for every occasion.`}</Consequence>
        )}
      </Container>

      {anchorStranded && (
        <Container top={6}>
          <Body7 isLowContrast>
            {`The saved anchor is ${range.anchor}, which this trigger does not provide. Pick one from the list, or switch the window to “relative to today”.`}
          </Body7>
        </Container>
      )}
    </div>
  );
};
